import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { softDelete, softDeleteHijos } from '../utils/softDelete';
import { useEsAdmin } from '../UserContext';
import TareaDetalle from '../components/TareaDetalle';
import { emitTareaChanged, onTareaChanged } from '../utils/tareasBus';
import ConfirmModal from '../components/ConfirmModal';
import ClienteDetalle from './ClienteDetalle';
import ClienteModal from '../components/ClienteModal';
import { useAsyncGuard } from '../utils/useAsyncGuard';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  pointerWithin, rectIntersection,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ─── Componentes módulo-nivel (referencia estable, React no los remmonta) ───

function SortableTarea({ id, cardProps }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Translate.toString(transform), transition };
  return (
    <TareaCard
      {...cardProps}
      setNodeRef={setNodeRef}
      sortableStyle={style}
      dragHandleProps={{ ...attributes, ...listeners }}
      sortableDragging={isDragging}
    />
  );
}

function SortableGrupo({
  id, nombre, memberTokens, tareasGrupo, colapsado, onToggleColapso,
  renombrando, renombreValor, setRenombreValor, onRenombrarStart, onRenombrarConfirm, onRenombrarCancel,
  onAsignarGrupo, onNuevaTarea, onEliminar, esAdmin, cardPropsFor,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Translate.toString(transform), transition,
    opacity: isDragging ? 0.5 : 1,
    marginBottom: 12, background: '#e2e6ea', border: '1px solid #c8cdd4', borderRadius: 10, overflow: 'hidden',
  };
  return (
    <div ref={setNodeRef} style={style}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer' }} onClick={onToggleColapso}>
        <span
          className="drag-handle"
          style={{ cursor: 'grab', fontSize: 14, color: 'var(--text4)', flexShrink: 0, lineHeight: 1, userSelect: 'none', touchAction: 'none', opacity: 1, padding: '2px 4px' }}
          {...attributes} {...listeners}
          onClick={e => e.stopPropagation()}
          title="Arrastrar grupo"
        >⠿</span>
        <span style={{ fontSize: 13 }}>📁</span>
        {renombrando ? (
          <input
            className="form-input"
            style={{ height: 24, padding: '0 6px', fontSize: 12, flex: 1 }}
            autoFocus value={renombreValor}
            onChange={e => setRenombreValor(e.target.value)}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') onRenombrarConfirm(); if (e.key === 'Escape') onRenombrarCancel(); }}
          />
        ) : (
          <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {nombre} ({tareasGrupo.length})
          </span>
        )}
        <span style={{ fontSize: 11, color: 'var(--text4)', marginLeft: 'auto' }}>{colapsado ? '▸' : '▾'}</span>
        <button className="btn btn-primary btn-xs" style={{ fontSize: 10, padding: '2px 8px' }} onClick={e => { e.stopPropagation(); onAsignarGrupo(); }}>Asignar</button>
        <button className="btn btn-ghost btn-xs" onClick={e => { e.stopPropagation(); onNuevaTarea(); }}>+ tarea</button>
        <button className="btn btn-ghost btn-xs" onClick={e => { e.stopPropagation(); onRenombrarStart(); }}>✎ Editar grupo</button>
        {esAdmin && <button className="btn btn-danger btn-xs" onClick={e => { e.stopPropagation(); onEliminar(); }}>🗑</button>}
      </div>
      {!colapsado && (
        <>
          <div style={{ height: '0.5px', background: 'var(--border)', margin: '0 8px' }} />
          <div style={{ padding: '6px 8px 8px' }}>
            <SortableContext items={memberTokens} strategy={verticalListSortingStrategy}>
              {tareasGrupo.map(t => (
                <SortableTarea key={'t:' + t.id} id={'t:' + t.id} cardProps={{ ...cardPropsFor(t), inGroup: true }} />
              ))}
              {tareasGrupo.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text4)', textAlign: 'center', padding: '12px 0' }}>Arrastrá tareas acá</div>
              )}
            </SortableContext>
          </div>
        </>
      )}
    </div>
  );
}

function SortableAudiencia({ id, cardProps }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Translate.toString(transform), transition };
  return (
    <AudienciaCard
      {...cardProps}
      setNodeRef={setNodeRef}
      sortableStyle={style}
      dragHandleProps={{ ...attributes, ...listeners }}
      sortableDragging={isDragging}
    />
  );
}

function SortableGrupoAudiencia({
  id, nombre, memberTokens, audsGrupo, colapsado, onToggleColapso,
  renombrando, renombreValor, setRenombreValor, onRenombrarStart, onRenombrarConfirm, onRenombrarCancel,
  onAsignarGrupo, onNuevaAudiencia, onEliminar, esAdmin, cardPropsFor,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Translate.toString(transform), transition,
    opacity: isDragging ? 0.5 : 1,
    marginBottom: 12, background: '#e2e6ea', border: '1px solid #c8cdd4', borderRadius: 10, overflow: 'hidden',
  };
  return (
    <div ref={setNodeRef} style={style}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer' }} onClick={onToggleColapso}>
        <span
          className="drag-handle"
          style={{ cursor: 'grab', fontSize: 14, color: 'var(--text4)', flexShrink: 0, lineHeight: 1, userSelect: 'none', touchAction: 'none', opacity: 1, padding: '2px 4px' }}
          {...attributes} {...listeners}
          onClick={e => e.stopPropagation()}
          title="Arrastrar grupo"
        >⠿</span>
        <span style={{ fontSize: 13 }}>📁</span>
        {renombrando ? (
          <input
            className="form-input"
            style={{ height: 24, padding: '0 6px', fontSize: 12, flex: 1 }}
            autoFocus value={renombreValor}
            onChange={e => setRenombreValor(e.target.value)}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') onRenombrarConfirm(); if (e.key === 'Escape') onRenombrarCancel(); }}
          />
        ) : (
          <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {nombre} ({audsGrupo.length})
          </span>
        )}
        <span style={{ fontSize: 11, color: 'var(--text4)', marginLeft: 'auto' }}>{colapsado ? '▸' : '▾'}</span>
        <button className="btn btn-primary btn-xs" style={{ fontSize: 10, padding: '2px 8px' }} onClick={e => { e.stopPropagation(); onAsignarGrupo(); }}>Asignar</button>
        <button className="btn btn-ghost btn-xs" onClick={e => { e.stopPropagation(); onNuevaAudiencia(); }}>+ audiencia</button>
        <button className="btn btn-ghost btn-xs" onClick={e => { e.stopPropagation(); onRenombrarStart(); }}>✎ Editar grupo</button>
        {esAdmin && <button className="btn btn-danger btn-xs" onClick={e => { e.stopPropagation(); onEliminar(); }}>🗑</button>}
      </div>
      {!colapsado && (
        <>
          <div style={{ height: '0.5px', background: 'var(--border)', margin: '0 8px' }} />
          <div style={{ padding: '6px 8px 8px' }}>
            <SortableContext items={memberTokens} strategy={verticalListSortingStrategy}>
              {audsGrupo.map(a => (
                <SortableAudiencia key={'a:' + a.id} id={'a:' + a.id} cardProps={{ ...cardPropsFor(a), inGroup: true }} />
              ))}
              {audsGrupo.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text4)', textAlign: 'center', padding: '12px 0' }}>Arrastrá audiencias acá</div>
              )}
            </SortableContext>
          </div>
        </>
      )}
    </div>
  );
}

function TareaCard({ tarea, inGroup, onToggle, onFijar, onEdit, onDelete, onDescartar, onReactivar, onVerDetalle, onAsignar, prioridadColor, formatDate, today, completada, descartada, setNodeRef, sortableStyle, dragHandleProps, sortableDragging, overlay }) {
  const esAdmin = useEsAdmin();
  const vencida = tarea.fecha_vencimiento && tarea.fecha_vencimiento < today && !completada && !descartada;
  const opacidad = completada || descartada ? 0.55 : 1;
  return (
    <div
      ref={setNodeRef}
      className="tarea-card"
      style={{
        ...(sortableStyle || {}),
        opacity: sortableDragging ? 0.4 : opacidad,
        boxShadow: overlay ? '0 8px 24px rgba(0,0,0,0.18)' : undefined,
        border: overlay ? '1px solid var(--blue)' : undefined,
        background: overlay ? 'var(--surface)' : undefined,
      }}
    >
      {dragHandleProps && (
        <div className="drag-handle" style={{ touchAction: 'none' }} {...dragHandleProps}>⠿</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, flexShrink: 0 }}>
        {!descartada && !completada && (
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 11, opacity: tarea.fijada ? 1 : 0.3, color: tarea.fijada ? 'var(--blue)' : 'var(--text4)', lineHeight: 1 }}
            onClick={() => onFijar?.(tarea)}
            title={tarea.fijada ? 'Desfijar' : 'Fijar'}
          >📌</button>
        )}
        {!descartada && (
          <input type="checkbox" className="tarea-check" checked={!!completada} onChange={() => onToggle?.(tarea)} onClick={e => e.stopPropagation()} style={{ marginTop: 0 }} />
        )}
        {descartada && (
          <div style={{ width: 16, height: 16, borderRadius: 3, border: '1.5px solid var(--text4)', flexShrink: 0 }} />
        )}
      </div>
      <div
        style={{ flex: 1, cursor: onVerDetalle ? 'pointer' : undefined }}
        onMouseDown={e => { e.currentTarget._mdx = e.clientX; e.currentTarget._mdy = e.clientY; }}
        onClick={e => {
          if (Math.abs(e.clientX - (e.currentTarget._mdx ?? e.clientX)) > 5 || Math.abs(e.clientY - (e.currentTarget._mdy ?? e.clientY)) > 5) return;
          onVerDetalle?.(tarea.id);
        }}
      >
        <div className="tarea-titulo" style={{ textDecoration: completada || descartada ? 'line-through' : 'none', color: descartada ? 'var(--text4)' : undefined }}>{tarea.titulo}</div>
        {tarea.descripcion && <div className="tarea-sub">{tarea.descripcion}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 5, flexWrap: 'wrap', alignItems: 'center' }}>
          {descartada && <span className="badge badge-gray" style={{ fontSize: 10 }}>Descartada</span>}
          {tarea.fijada && !descartada && <span className="badge badge-blue" style={{ fontSize: 10 }}>📌 Fijada</span>}
          {tarea.prioridad === 'urgente' && !descartada && <span className="badge badge-red" style={{ fontSize: 10 }}>Urgente</span>}
          {tarea.prioridad === 'muy importante' && !descartada && <span className="badge" style={{ fontSize: 10, background: '#fef3c7', color: '#b45309', border: '1px solid #fcd34d' }}>Muy importante</span>}
          {tarea.fecha && (
            <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 20, background: '#eff6ff', color: '#3b82f6', border: '0.5px solid #bfdbfe', fontWeight: 500 }}>
              Agendado para el día {formatDate(tarea.fecha)}
            </span>
          )}
          {tarea.fecha_vencimiento && (
            <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 20, background: vencida ? '#fee2e2' : '#fefce8', color: vencida ? '#dc2626' : '#ca8a04', border: `0.5px solid ${vencida ? '#fecaca' : '#fde68a'}`, fontWeight: 500 }}>
              Vencimiento {formatDate(tarea.fecha_vencimiento)}{vencida ? ' — VENCIDA' : ''}
            </span>
          )}
          {completada && tarea.completada_en && (
            <span style={{ fontSize: 10, color: 'var(--text4)' }}>
              ✓ {new Date(tarea.completada_en).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>
      <div className="flex gap-1">
        {descartada ? (
          <button className="btn btn-ghost btn-xs" style={{ fontSize: 10, padding: '2px 8px' }} onClick={() => onReactivar?.(tarea.id)}>Reactivar</button>
        ) : (
          <>
            {onAsignar && (
              <button className="btn btn-primary btn-xs" style={{ fontSize: 10, padding: '2px 8px' }} onClick={onAsignar}>Asignar</button>
            )}
            <button className="btn btn-ghost btn-xs" style={{ color: 'var(--text4)', fontSize: 10, padding: '2px 7px' }} onClick={() => onDescartar?.(tarea.id)}>Descartar</button>
          </>
        )}
        {esAdmin && onDelete && <button className="btn btn-danger btn-xs" onClick={() => onDelete(tarea.id)}>🗑</button>}
      </div>
    </div>
  );
}

function AudienciaCard({ a, inGroup, onClickCard, onClickAsignar, onCambiarEstado, onDelete, esAdmin, setNodeRef, sortableStyle, dragHandleProps, sortableDragging, overlay }) {
  const colorTipo = (tipo) => {
    const map = { 'Audiencia': '#ef4444', 'Vencimiento procesal': '#f59e0b', 'Consulta / Turno': '#10b981', 'Reunión': '#3b82f6' };
    return map[tipo] || '#9ca3af';
  };
  const todayStr = new Date().toISOString().split('T')[0];
  const esPasadaPendiente = a.fecha && a.fecha < todayStr && a.estado === 'pendiente';
  const realizada = a.estado === 'realizado';
  const cancelada = a.estado === 'cancelado';
  return (
    <div
      ref={setNodeRef}
      className="audiencia-drag-card"
      style={{
        ...(sortableStyle || {}),
        display: 'flex', gap: 12, padding: '10px 12px',
        background: overlay ? 'var(--surface)' : inGroup ? 'var(--surface, #fff)' : 'var(--surface2)',
        border: overlay ? '1px solid var(--blue)' : inGroup ? 'none' : '0.5px solid var(--border)',
        opacity: sortableDragging ? 0.4 : (realizada || cancelada) ? 0.65 : 1,
        borderLeft: `3px solid ${colorTipo(a.tipo)}`,
        borderRadius: 8, marginBottom: 4, alignItems: 'flex-start',
        cursor: 'pointer',
        boxShadow: overlay ? '0 8px 24px rgba(0,0,0,0.18)' : undefined
      }}
      onMouseDown={e => { e.currentTarget._mdx = e.clientX; e.currentTarget._mdy = e.clientY; }}
      onClick={e => {
        if (Math.abs(e.clientX - (e.currentTarget._mdx ?? e.clientX)) > 5 || Math.abs(e.clientY - (e.currentTarget._mdy ?? e.clientY)) > 5) return;
        onClickCard?.();
      }}
    >
      {dragHandleProps && (
        <div className="drag-handle" style={{ touchAction: 'none', alignSelf: 'center' }} {...dragHandleProps} onClick={e => e.stopPropagation()}>⠿</div>
      )}
      <div style={{ minWidth: 72, paddingTop: 1 }}>
        <div style={{ fontFamily: 'DM Mono', fontSize: 11, color: colorTipo(a.tipo), fontWeight: 600 }}>
          {a.fecha ? new Date(a.fecha + 'T12:00:00').toLocaleDateString('es-AR') : '—'}
        </div>
        {a.hora && <div style={{ fontSize: 11, color: 'var(--text4)' }}>{a.hora.substring(0, 5)}</div>}
        {esPasadaPendiente && <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 700, marginTop: 2 }}>Ya pasó</div>}
      </div>
      <div style={{ flex: 1, cursor: 'pointer', minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{a.titulo}</div>
        <div style={{ marginTop: 3 }}>
          <span style={{ background: colorTipo(a.tipo) + '18', color: colorTipo(a.tipo), padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 600 }}>{a.tipo}</span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {realizada ? (
            <>
              <span className="badge badge-green" style={{ fontSize: 10 }}>Realizado</span>
              {onCambiarEstado && (
                <button onClick={() => onCambiarEstado('pendiente')} title="Volver a pendiente"
                  style={{ background: 'none', border: 'none', color: 'var(--text4)', fontSize: 11, cursor: 'pointer', padding: '0 2px', lineHeight: 1 }}>↩</button>
              )}
            </>
          ) : cancelada ? (
            <>
              <span className="badge badge-red" style={{ fontSize: 10 }}>Cancelado</span>
              {onCambiarEstado && (
                <button onClick={() => onCambiarEstado('pendiente')} title="Volver a pendiente"
                  style={{ background: 'none', border: 'none', color: 'var(--text4)', fontSize: 11, cursor: 'pointer', padding: '0 2px', lineHeight: 1 }}>↩</button>
              )}
            </>
          ) : esPasadaPendiente ? (
            <>
              <button onClick={() => onCambiarEstado?.('realizado')}
                style={{ background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, padding: '2px 7px', fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                ✓ Realizada
              </button>
              <button onClick={() => onCambiarEstado?.('cancelado')}
                style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 6, padding: '2px 7px', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
                ✕ Cancelar
              </button>
            </>
          ) : (
            <>
              <span className="badge badge-gray" style={{ fontSize: 10 }}>Pendiente</span>
              <button onClick={() => onCambiarEstado?.('cancelado')} title="Marcar como cancelada"
                style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 6, padding: '2px 7px', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
                ✕ Cancelar
              </button>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <button className="btn btn-primary btn-xs" style={{ fontSize: 10, padding: '2px 8px' }} onClick={() => onClickAsignar?.()}>Asignar</button>
          {esAdmin && onDelete && (
            <button className="btn btn-danger btn-xs" onClick={onDelete} title="Eliminar">🗑</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export default function TramiteDetalle({ tramiteId, onClose, onUpdate, backLabel }) {
  const esAdmin = useEsAdmin();
  const [tramite, setTramite] = useState(null);
  const [tareas, setTareas] = useState([]);
  const [audiencias, setAudiencias] = useState([]);
  const [notas, setNotas] = useState([]);
  const [nuevaNota, setNuevaNota] = useState('');
  const [loading, setLoading] = useState(true);
  const [tabActiva, setTabActiva] = useState('tareas');
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);

  // Tareas
  const [modalTareaOpen, setModalTareaOpen] = useState(false);
  const [editingTareaId, setEditingTareaId] = useState(null);
  const [mostrarCompletadas, setMostrarCompletadas] = useState(false);
  const [mostrarDescartadas, setMostrarDescartadas] = useState(false);
  const [tareaDetalleId, setTareaDetalleId] = useState(null);
  const [verCliente, setVerCliente] = useState(null);
  const [clienteEdit, setClienteEdit] = useState(null);
  const [guardandoTarea, guardTarea] = useAsyncGuard();
  const [guardandoAudiencia, guardAudiencia] = useAsyncGuard();
  const [toggling, setToggling] = useState(new Set());
  const [formTarea, setFormTarea] = useState({ titulo: '', descripcion: '', fecha: '', fecha_vencimiento: '', prioridad: 'normal', fijada: false, grupo: '' });
  const [notasTarea, setNotasTarea] = useState([]);
  const [nuevaNotaTarea, setNuevaNotaTarea] = useState('');
  const [formTareaGrupoNuevo, setFormTareaGrupoNuevo] = useState(false);
  const [formTareaGrupoNuevoValor, setFormTareaGrupoNuevoValor] = useState('');

  // Grupos tareas
  const [gruposColapsados, setGruposColapsados] = useState({});
  const [gruposOrden, setGruposOrden] = useState([]);
  const [nuevoGrupoInlineOpen, setNuevoGrupoInlineOpen] = useState(false);
  const [nuevoGrupoNombre, setNuevoGrupoNombre] = useState('');
  const [renombrandoGrupo, setRenombrandoGrupo] = useState(null);
  const [renombreValor, setRenombreValor] = useState('');

  // @dnd-kit tareas
  const [activeDragId, setActiveDragId] = useState(null);
  const [contenedoresDrag, setContenedoresDrag] = useState(null);
  const contenedoresRef = useRef(null);

  // Audiencias
  const [modalAudienciaOpen, setModalAudienciaOpen] = useState(false);
  const [editingAudienciaId, setEditingAudienciaId] = useState(null);
  const [audienciaError, setAudienciaError] = useState('');
  const [formAudiencia, setFormAudiencia] = useState({ titulo: '', tipo: 'Audiencia', fecha: '', hora: '00:00', notas: '', estado: 'pendiente', grupo: '' });
  const [mostrarRealizadas, setMostrarRealizadas] = useState(false);
  const [mostrarCanceladas, setMostrarCanceladas] = useState(false);
  const [formAudienciaGrupoNuevo, setFormAudienciaGrupoNuevo] = useState(false);
  const [formAudienciaGrupoNuevoValor, setFormAudienciaGrupoNuevoValor] = useState('');

  // Grupos audiencias
  const [gruposAudienciasColapsados, setGruposAudienciasColapsados] = useState({});
  const [gruposAudienciasOrden, setGruposAudienciasOrden] = useState([]);
  const [nuevoGrupoAudienciaOpen, setNuevoGrupoAudienciaOpen] = useState(false);
  const [nuevoGrupoAudienciaNombre, setNuevoGrupoAudienciaNombre] = useState('');
  const [renombrandoGrupoAudiencia, setRenombrandoGrupoAudiencia] = useState(null);
  const [renombreAudienciaValor, setRenombreAudienciaValor] = useState('');

  // @dnd-kit audiencias
  const [activeDragAudId, setActiveDragAudId] = useState(null);
  const [contenedoresAudDrag, setContenedoresAudDrag] = useState(null);
  const contenedoresAudRef = useRef(null);

  // Modal asignar
  const [modalAsignarOpen, setModalAsignarOpen] = useState(false);
  const [asignarItems, setAsignarItems] = useState([]);
  const [asignarTitulo, setAsignarTitulo] = useState('');
  const [asignarTipo, setAsignarTipo] = useState('tarea');
  const [asignarTab, setAsignarTab] = useState('nueva');
  const [asignarForm, setAsignarForm] = useState({ titulo: '', fecha: '', asignados_a: [] });
  const [asignarUsuarios, setAsignarUsuarios] = useState([]);
  const [asignarAsignaciones, setAsignarAsignaciones] = useState([]);
  const [asignarExistenteId, setAsignarExistenteId] = useState(null);
  const [asignarBusqueda, setAsignarBusqueda] = useState('');
  const [asignarGuardando, setAsignarGuardando] = useState(false);

  // Editar trámite (ficha)
  const [modalEditarTramite, setModalEditarTramite] = useState(false);
  const [formEditarTramite, setFormEditarTramite] = useState({});
  const [guardandoTramite, setGuardandoTramite] = useState(false);

  // Trámite vinculado (principal / sub-trámites)
  const [subTramites, setSubTramites] = useState([]);
  const [busquedaTramiteVincular, setBusquedaTramiteVincular] = useState('');
  const [resultadosTramiteVincular, setResultadosTramiteVincular] = useState([]);
  const [dropdownTramiteVincularOpen, setDropdownTramiteVincularOpen] = useState(false);

  useEffect(() => {
    if (tramiteId) { fetchTramite(); fetchTareas(); fetchAudiencias(); fetchNotas(); fetchSubTramites(); }
  }, [tramiteId]);

  // Mantenerse sincronizado cuando una tarea cambia desde otro lugar
  useEffect(() => onTareaChanged(() => { if (tramiteId) fetchTareas(); }), [tramiteId]);

  // Sincronizar grupos de tareas desde DB
  useEffect(() => {
    const gruposDB = [...new Set(tareas.map(t => t.grupo).filter(Boolean))];
    setGruposOrden(prev => {
      const nuevos = gruposDB.filter(g => !prev.includes(g));
      return nuevos.length ? [...prev, ...nuevos] : prev;
    });
  }, [tareas]);

  // Sincronizar grupos de audiencias desde DB
  useEffect(() => {
    const gruposDB = [...new Set(audiencias.map(a => a.grupo).filter(Boolean))];
    setGruposAudienciasOrden(prev => {
      const nuevos = gruposDB.filter(g => !prev.includes(g));
      return nuevos.length ? [...prev, ...nuevos] : prev;
    });
  }, [audiencias]);

  async function fetchTramite() {
    const { data } = await supabase.from('tramites')
      .select('*, tramites_clientes(cliente_id, clientes(nombre, apellido)), expedientes(numero, caratula), tramite_principal:tramites!tramite_principal_id(id, nombre, tipo, estado)')
      .eq('id', tramiteId).single();
    setTramite(data);
    setLoading(false);
  }

  async function fetchSubTramites() {
    const { data } = await supabase.from('tramites')
      .select('id, nombre, tipo, estado')
      .eq('tramite_principal_id', tramiteId);
    setSubTramites(data || []);
  }

  async function fetchTareas() {
    let { data, error } = await supabase.from('tareas').select('*')
      .eq('tramite_id', tramiteId)
      .eq('eliminado', false)
      .order('orden', { ascending: true, nullsFirst: false });
    if (error) {
      ({ data } = await supabase.from('tareas').select('*').eq('tramite_id', tramiteId).eq('eliminado', false).order('created_at', { ascending: true }));
    }
    setTareas(data || []);
  }

  async function fetchAudiencias() {
    let { data, error } = await supabase.from('agenda').select('*')
      .eq('tramite_id', tramiteId)
      .eq('eliminado', false)
      .order('orden', { ascending: true, nullsFirst: false })
      .order('fecha', { ascending: true });
    if (error) {
      ({ data } = await supabase.from('agenda').select('*').eq('tramite_id', tramiteId).eq('eliminado', false).order('fecha', { ascending: true }));
    }
    setAudiencias(data || []);
  }

  async function fetchNotas() {
    const { data } = await supabase.from('tramites_notas').select('*').eq('tramite_id', tramiteId).eq('eliminado', false).order('created_at', { ascending: false });
    setNotas(data || []);
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3000); }

  // ── Tareas ──

  function openNuevaTarea(grupoInicial = '') {
    setEditingTareaId(null);
    setFormTarea({ titulo: '', descripcion: '', fecha: '', fecha_vencimiento: '', prioridad: 'normal', fijada: false, grupo: grupoInicial });
    setFormTareaGrupoNuevo(false);
    setFormTareaGrupoNuevoValor('');
    setModalTareaOpen(true);
  }

  function openEditTarea(t) {
    setEditingTareaId(t.id);
    setFormTarea({ titulo: t.titulo || '', descripcion: t.descripcion || '', fecha: t.fecha || '', fecha_vencimiento: t.fecha_vencimiento || '', prioridad: t.prioridad || 'normal', fijada: t.fijada || false, grupo: t.grupo || '' });
    setFormTareaGrupoNuevo(false);
    setFormTareaGrupoNuevoValor('');
    setNuevaNotaTarea('');
    fetchNotasTarea(t.id);
    setModalTareaOpen(true);
  }

  async function fetchNotasTarea(tareaId) {
    const { data } = await supabase.from('tareas_notas').select('*').eq('tarea_id', tareaId).eq('eliminado', false).order('created_at', { ascending: false });
    setNotasTarea(data || []);
  }

  async function addNotaTarea() {
    if (!nuevaNotaTarea.trim() || !editingTareaId) return;
    await supabase.from('tareas_notas').insert({ tarea_id: editingTareaId, contenido: nuevaNotaTarea.trim() });
    setNuevaNotaTarea('');
    fetchNotasTarea(editingTareaId);
  }

  async function deleteNotaTarea(id) {
    await softDelete('tareas_notas', id);
    setNotasTarea(ns => ns.filter(n => n.id !== id));
  }

  async function saveTarea() {
    if (!formTarea.titulo.trim()) { showToast('El título es obligatorio'); return; }
    const grupoFinal = formTareaGrupoNuevo ? (formTareaGrupoNuevoValor.trim() || null) : (formTarea.grupo || null);
    const tareaData = { titulo: formTarea.titulo, descripcion: formTarea.descripcion, fecha: formTarea.fecha || null, fecha_vencimiento: formTarea.fecha_vencimiento || null, prioridad: formTarea.prioridad, fijada: formTarea.fijada, tramite_id: tramiteId, completada: false, grupo: grupoFinal };
    if (editingTareaId) {
      await supabase.from('tareas').update(tareaData).eq('id', editingTareaId);
      await supabase.from('asignacion_items').update({ descripcion: formTarea.titulo }).eq('tarea_id', editingTareaId);
    } else {
      await supabase.from('tareas').insert(tareaData);
    }
    showToast(editingTareaId ? 'Tarea actualizada ✓' : 'Tarea creada ✓');
    setModalTareaOpen(false);
    fetchTareas();
  }

  async function toggleTarea(tarea) {
    if (toggling.has(tarea.id)) return;
    const completada = !tarea.completada;
    const completada_en = completada ? new Date().toISOString() : null;
    setTareas(prev => prev.map(t => t.id === tarea.id ? { ...t, completada, completada_en, descartada: false } : t));
    setToggling(prev => new Set([...prev, tarea.id]));
    await supabase.from('tareas').update({ completada, descartada: false, completada_en }).eq('id', tarea.id);
    setToggling(prev => { const s = new Set(prev); s.delete(tarea.id); return s; });
    emitTareaChanged({ id: tarea.id, completada });
  }

  async function toggleFijada(tarea) {
    await supabase.from('tareas').update({ fijada: !tarea.fijada }).eq('id', tarea.id);
    fetchTareas();
  }

  async function descartarTarea(id) {
    await supabase.from('tareas').update({ descartada: true, completada: false, descartada_en: new Date().toISOString() }).eq('id', id);
    fetchTareas();
    emitTareaChanged({ id, descartada: true });
  }

  async function reactivarTarea(id) {
    await supabase.from('tareas').update({ descartada: false, descartada_en: null }).eq('id', id);
    fetchTareas();
    emitTareaChanged({ id, descartada: false });
  }

  async function deleteTarea(id) {
    setConfirm({
      titulo: 'Eliminar tarea', mensaje: '¿Eliminar esta tarea?',
      onConfirmar: async () => { setConfirm(null); await softDeleteHijos('tareas_notas', 'tarea_id', id); await softDelete('tareas', id); fetchTareas(); emitTareaChanged({ id, eliminado: true }); }
    });
  }

  async function renombrarGrupo(viejo, nuevo) {
    if (!nuevo.trim() || nuevo === viejo) return;
    await supabase.from('tareas').update({ grupo: nuevo.trim() }).eq('tramite_id', tramiteId).eq('grupo', viejo);
    setGruposOrden(prev => prev.map(g => g === viejo ? nuevo.trim() : g));
    setRenombrandoGrupo(null);
    setRenombreValor('');
    fetchTareas();
  }

  async function eliminarGrupo(nombre) {
    const esLocal = !tareas.some(t => t.grupo === nombre);
    if (esLocal) { setGruposOrden(prev => prev.filter(g => g !== nombre)); return; }
    setConfirm({
      titulo: 'Eliminar grupo',
      mensaje: `¿Eliminar el grupo "${nombre}"? Las tareas quedarán sin grupo.`,
      onConfirmar: async () => {
        setConfirm(null);
        await supabase.from('tareas').update({ grupo: null }).eq('tramite_id', tramiteId).eq('grupo', nombre);
        setGruposOrden(prev => prev.filter(g => g !== nombre));
        fetchTareas();
      }
    });
  }

  function crearGrupoInline() {
    if (!nuevoGrupoNombre.trim()) return;
    const nombre = nuevoGrupoNombre.trim();
    setGruposOrden(prev => [nombre, ...prev.filter(g => g !== nombre)]);
    setNuevoGrupoInlineOpen(false);
    setNuevoGrupoNombre('');
  }

  // ── Audiencias ──

  async function saveAudiencia() {
    if (!formAudiencia.titulo.trim()) { setAudienciaError('El título es obligatorio.'); return; }
    if (!formAudiencia.fecha) { setAudienciaError('La fecha es obligatoria.'); return; }
    setAudienciaError('');
    const grupoFinal = formAudienciaGrupoNuevo ? (formAudienciaGrupoNuevoValor.trim() || null) : (formAudiencia.grupo || null);
    const payload = { ...formAudiencia, hora: formAudiencia.hora || null, grupo: grupoFinal };
    if (editingAudienciaId) {
      await supabase.from('agenda').update(payload).eq('id', editingAudienciaId);
      showToast('Evento actualizado ✓');
    } else {
      await supabase.from('agenda').insert({ ...payload, tramite_id: tramiteId });
      showToast('Evento agendado ✓');
    }
    setModalAudienciaOpen(false);
    setEditingAudienciaId(null);
    setFormAudiencia({ titulo: '', tipo: 'Audiencia', fecha: '', hora: '00:00', notas: '', estado: 'pendiente', grupo: '' });
    setFormAudienciaGrupoNuevo(false);
    setFormAudienciaGrupoNuevoValor('');
    fetchAudiencias();
  }

  async function setEstadoAudiencia(id, estado) {
    setAudiencias(prev => prev.map(a => a.id === id ? { ...a, estado } : a));
    const { error } = await supabase.from('agenda').update({ estado }).eq('id', id);
    if (error) fetchAudiencias();
  }

  async function deleteAudiencia(id) {
    setConfirm({
      titulo: 'Eliminar audiencia', mensaje: '¿Eliminar esta audiencia/turno? Esta acción no se puede deshacer.',
      onConfirmar: async () => {
        setConfirm(null);
        setAudiencias(prev => prev.filter(a => a.id !== id));
        await softDelete('agenda', id);
      }
    });
  }

  async function renombrarGrupoAudiencia(viejo, nuevo) {
    if (!nuevo.trim() || nuevo === viejo) return;
    await supabase.from('agenda').update({ grupo: nuevo.trim() }).eq('tramite_id', tramiteId).eq('grupo', viejo);
    setGruposAudienciasOrden(prev => prev.map(g => g === viejo ? nuevo.trim() : g));
    setRenombrandoGrupoAudiencia(null);
    setRenombreAudienciaValor('');
    fetchAudiencias();
  }

  async function eliminarGrupoAudiencia(nombre) {
    const esLocal = !audiencias.some(a => a.grupo === nombre);
    if (esLocal) { setGruposAudienciasOrden(prev => prev.filter(g => g !== nombre)); return; }
    setConfirm({
      titulo: 'Eliminar grupo',
      mensaje: `¿Eliminar el grupo "${nombre}"? Las audiencias quedarán sin grupo.`,
      onConfirmar: async () => {
        setConfirm(null);
        await supabase.from('agenda').update({ grupo: null }).eq('tramite_id', tramiteId).eq('grupo', nombre);
        setGruposAudienciasOrden(prev => prev.filter(g => g !== nombre));
        fetchAudiencias();
      }
    });
  }

  function crearGrupoAudienciaInline() {
    if (!nuevoGrupoAudienciaNombre.trim()) return;
    const nombre = nuevoGrupoAudienciaNombre.trim();
    setGruposAudienciasOrden(prev => [nombre, ...prev.filter(g => g !== nombre)]);
    setNuevoGrupoAudienciaOpen(false);
    setNuevoGrupoAudienciaNombre('');
  }

  // ── Asignar ──

  function openModalAsignar(items, titulo, tipo = 'tarea') {
    setAsignarItems(items);
    setAsignarTitulo(titulo);
    setAsignarTipo(tipo);
    setAsignarTab('nueva');
    setAsignarForm({ titulo, fecha: new Date().toISOString().split('T')[0], asignados_a: [] });
    setAsignarExistenteId(null);
    setAsignarBusqueda('');
    setModalAsignarOpen(true);
    fetchAsignarUsuarios();
    fetchAsignarAsignaciones();
  }

  async function fetchAsignarUsuarios() {
    const { data } = await supabase.from('profiles').select('id, nombre, rol').order('nombre');
    if (!data || data.length === 0) {
      const { data: { session } } = await supabase.auth.getSession();
      setAsignarUsuarios(session ? [{ id: session.user.id, nombre: session.user.email?.split('@')[0] || 'Mi usuario' }] : []);
    } else {
      setAsignarUsuarios(data.map(u => ({ ...u, nombre: u.nombre || u.rol || 'Sin nombre' })));
    }
  }

  async function fetchAsignarAsignaciones() {
    const { data } = await supabase.from('asignaciones').select('id, titulo, fecha').eq('eliminado', false).order('fecha', { ascending: false }).limit(80);
    setAsignarAsignaciones(data || []);
  }

  async function guardarAsignar() {
    setAsignarGuardando(true);
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    const esAudiencia = asignarTipo === 'audiencia';
    if (asignarTab === 'nueva') {
      if (!asignarForm.titulo.trim() || !asignarForm.fecha || asignarForm.asignados_a.length === 0) {
        showToast('Completá título, fecha y al menos un usuario'); setAsignarGuardando(false); return;
      }
      const { data: nueva, error } = await supabase.from('asignaciones').insert({ titulo: asignarForm.titulo.trim(), fecha: asignarForm.fecha, creado_por: userId }).select('id').single();
      if (error) { showToast('Error: ' + error.message); setAsignarGuardando(false); return; }
      await supabase.from('asignacion_usuarios').insert(asignarForm.asignados_a.map(uid => ({ asignacion_id: nueva.id, usuario_id: uid })));
      await supabase.from('asignacion_items').insert(asignarItems.map((t, i) => ({ asignacion_id: nueva.id, descripcion: t.titulo, ...(esAudiencia ? { agenda_id: t.id } : { tarea_id: t.id }), orden: i })));
      showToast('Asignación creada ✓');
    } else {
      if (!asignarExistenteId) { showToast('Seleccioná una asignación'); setAsignarGuardando(false); return; }
      const idField = esAudiencia ? 'agenda_id' : 'tarea_id';
      const { data: existingItems } = await supabase.from('asignacion_items').select(`${idField}, orden`).eq('asignacion_id', asignarExistenteId);
      const existingIds = new Set((existingItems || []).map(i => i[idField]).filter(Boolean));
      const maxOrden = existingItems?.length ? Math.max(...existingItems.map(i => i.orden)) + 1 : 0;
      const newItems = asignarItems.filter(t => !existingIds.has(t.id)).map((t, i) => ({ asignacion_id: asignarExistenteId, descripcion: t.titulo, ...(esAudiencia ? { agenda_id: t.id } : { tarea_id: t.id }), orden: maxOrden + i }));
      if (newItems.length > 0) await supabase.from('asignacion_items').insert(newItems);
      showToast('Tareas agregadas ✓');
    }
    setAsignarGuardando(false);
    setModalAsignarOpen(false);
  }

  // ── Notas del trámite ──

  async function addNota() {
    if (!nuevaNota.trim()) return;
    const { error } = await supabase.from('tramites_notas').insert({ tramite_id: tramiteId, contenido: nuevaNota.trim() });
    if (error) { showToast('Error al guardar la nota'); return; }
    setNuevaNota('');
    fetchNotas();
    showToast('Nota guardada ✓');
  }

  async function deleteNota(id) {
    await softDelete('tramites_notas', id);
    setNotas(ns => ns.filter(n => n.id !== id));
  }

  // ── Editar ficha del trámite ──

  function openEditarTramite() {
    setFormEditarTramite({
      nombre: tramite.nombre || '',
      tipo: tramite.tipo || '',
      estado: tramite.estado || 'pendiente',
      fecha_inicio: tramite.fecha_inicio || '',
      fecha_vencimiento: tramite.fecha_vencimiento || '',
      parte_contraria: tramite.parte_contraria || '',
      resultado: tramite.resultado || '',
      tramite_principal_id: tramite.tramite_principal_id || null,
    });
    setBusquedaTramiteVincular('');
    setResultadosTramiteVincular(tramite.tramite_principal ? [tramite.tramite_principal] : []);
    setModalEditarTramite(true);
  }

  async function buscarTramitesVincular(texto) {
    if (!texto.trim()) { setResultadosTramiteVincular([]); setDropdownTramiteVincularOpen(false); return; }
    const { data } = await supabase.from('tramites')
      .select('id, nombre, tipo, estado')
      .ilike('nombre', `%${texto.trim()}%`)
      .neq('id', tramiteId)
      .limit(10);
    setResultadosTramiteVincular(data || []);
    setDropdownTramiteVincularOpen(true);
  }

  async function guardarEditarTramite() {
    if (!formEditarTramite.nombre.trim()) { showToast('El nombre es obligatorio'); return; }
    setGuardandoTramite(true);
    await supabase.from('tramites').update({
      nombre: formEditarTramite.nombre.trim(),
      tipo: formEditarTramite.tipo || null,
      estado: formEditarTramite.estado,
      fecha_inicio: formEditarTramite.fecha_inicio || null,
      fecha_vencimiento: formEditarTramite.fecha_vencimiento || null,
      parte_contraria: formEditarTramite.parte_contraria || null,
      resultado: formEditarTramite.resultado || null,
      tramite_principal_id: formEditarTramite.tramite_principal_id || null,
    }).eq('id', tramiteId);
    setGuardandoTramite(false);
    setModalEditarTramite(false);
    showToast('Trámite actualizado ✓');
    fetchTramite();
    fetchSubTramites();
    onUpdate?.();
  }

  // ── @dnd-kit tareas ──

  const tareasPendientes = tareas.filter(t => !t.completada && !t.descartada);
  const tareasCompletadas = tareas.filter(t => t.completada && !t.descartada);
  const tareasDescartadas = tareas.filter(t => t.descartada);
  const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR') : '—';
  const today = new Date().toISOString().split('T')[0];
  const prioridadColor = (p) => ({ urgente: 'var(--red)', 'muy importante': '#f59e0b', normal: 'var(--blue)', baja: 'var(--text4)' }[p] || 'var(--blue)');
  const grupos = gruposOrden.filter(g => tareasPendientes.some(t => t.grupo === g) || !tareas.some(t => t.grupo === g));

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const tareasPendientesOrden = [...tareasPendientes].sort((a, b) => (a.orden ?? 1e9) - (b.orden ?? 1e9));

  function buildContenedores() {
    const ordenTop = [];
    const gruposMap = {};
    const placed = new Set();
    for (const t of tareasPendientesOrden) {
      if (t.grupo) {
        if (!gruposMap[t.grupo]) gruposMap[t.grupo] = [];
        gruposMap[t.grupo].push('t:' + t.id);
        if (!placed.has('g:' + t.grupo)) { placed.add('g:' + t.grupo); ordenTop.push('g:' + t.grupo); }
      } else {
        ordenTop.push('t:' + t.id);
      }
    }
    const vacios = gruposOrden
      .filter(g => !gruposMap[g] && !tareas.some(t => t.grupo === g))
      .map(g => { gruposMap[g] = []; return 'g:' + g; });
    ordenTop.unshift(...vacios);
    return { ordenTop, grupos: gruposMap };
  }

  const vista = contenedoresDrag || buildContenedores();

  function findContenedor(token, c) {
    if (token.startsWith('g:')) return '__top__';
    if (c.ordenTop.includes(token)) return '__top__';
    for (const nombre in c.grupos) if (c.grupos[nombre].includes(token)) return 'g:' + nombre;
    return null;
  }

  function handleDndStart({ active }) {
    const c = buildContenedores();
    contenedoresRef.current = c;
    setContenedoresDrag(c);
    setActiveDragId(active.id);
  }

  function handleDndOver({ active, over }) {
    if (!over) return;
    const activeId = active.id; const overId = over.id;
    if (activeId.startsWith('g:')) return;
    const c = contenedoresRef.current;
    if (!c) return;
    const desde = findContenedor(activeId, c);
    let hacia = overId.startsWith('g:') ? overId : findContenedor(overId, c);
    if (!desde || !hacia || desde === hacia) return;
    const next = { ordenTop: [...c.ordenTop], grupos: {} };
    for (const k in c.grupos) next.grupos[k] = [...c.grupos[k]];
    if (desde === '__top__') { const i = next.ordenTop.indexOf(activeId); if (i >= 0) next.ordenTop.splice(i, 1); }
    else { const arr = next.grupos[desde.slice(2)]; const i = arr.indexOf(activeId); if (i >= 0) arr.splice(i, 1); }
    if (hacia === '__top__') { const i = next.ordenTop.indexOf(overId); next.ordenTop.splice(i >= 0 ? i : next.ordenTop.length, 0, activeId); }
    else { const arr = next.grupos[hacia.slice(2)]; const i = arr.indexOf(overId); arr.splice(i >= 0 ? i : arr.length, 0, activeId); }
    contenedoresRef.current = next;
    setContenedoresDrag(next);
  }

  function handleDndEnd({ active, over }) {
    const c = contenedoresRef.current;
    const activeId = active.id;
    if (c && over) {
      const overId = over.id;
      const next = { ordenTop: [...c.ordenTop], grupos: {} };
      for (const k in c.grupos) next.grupos[k] = [...c.grupos[k]];
      if (activeId.startsWith('g:')) {
        const from = next.ordenTop.indexOf(activeId);
        let destinoToken = overId;
        if (!next.ordenTop.includes(destinoToken)) destinoToken = findContenedor(overId, next) === '__top__' ? overId : null;
        const to = destinoToken ? next.ordenTop.indexOf(destinoToken) : -1;
        if (from >= 0 && to >= 0) next.ordenTop = arrayMove(next.ordenTop, from, to);
      } else {
        const cont = findContenedor(activeId, next);
        const arr = cont === '__top__' ? next.ordenTop : next.grupos[cont.slice(2)];
        const from = arr.indexOf(activeId);
        const to = arr.indexOf(overId);
        if (from >= 0 && to >= 0) { const moved = arrayMove(arr, from, to); if (cont === '__top__') next.ordenTop = moved; else next.grupos[cont.slice(2)] = moved; }
      }
      persistirOrden(next);
    }
    contenedoresRef.current = null;
    setContenedoresDrag(null);
    setActiveDragId(null);
  }

  function persistirOrden(c) {
    const flat = [];
    const ordenGrupos = [];
    for (const token of c.ordenTop) {
      if (token.startsWith('t:')) { flat.push({ id: token.slice(2), grupo: null }); }
      else { const nombre = token.slice(2); ordenGrupos.push(nombre); for (const mt of (c.grupos[nombre] || [])) flat.push({ id: mt.slice(2), grupo: nombre }); }
    }
    const updates = flat.map((f, i) => ({ id: f.id, grupo: f.grupo, orden: i }));
    const byId = Object.fromEntries(updates.map(u => [u.id, u]));
    setTareas(prev => prev.map(t => byId[t.id] ? { ...t, grupo: byId[t.id].grupo, orden: byId[t.id].orden } : t).sort((a, b) => (a.orden ?? 1e9) - (b.orden ?? 1e9)));
    setGruposOrden(prev => { const resto = prev.filter(g => !ordenGrupos.includes(g)); return [...ordenGrupos, ...resto]; });
    Promise.all(updates.map(u => supabase.from('tareas').update({ grupo: u.grupo, orden: u.orden }).eq('id', u.id))).catch(() => fetchTareas());
  }

  const colisionDnd = (args) => { const p = pointerWithin(args); return p.length > 0 ? p : rectIntersection(args); };

  // ── @dnd-kit audiencias ──

  const audienciasVisibles = audiencias.filter(a => a.estado !== 'realizado' && a.estado !== 'cancelado');
  const audienciasVisiblesOrden = [...audienciasVisibles].sort((a, b) => (a.orden ?? 1e9) - (b.orden ?? 1e9));

  function buildContenedoresAud() {
    const ordenTop = [];
    const gruposMap = {};
    const placed = new Set();
    for (const a of audienciasVisiblesOrden) {
      if (a.grupo) {
        if (!gruposMap[a.grupo]) gruposMap[a.grupo] = [];
        gruposMap[a.grupo].push('a:' + a.id);
        if (!placed.has('g:' + a.grupo)) { placed.add('g:' + a.grupo); ordenTop.push('g:' + a.grupo); }
      } else {
        ordenTop.push('a:' + a.id);
      }
    }
    const vacios = gruposAudienciasOrden
      .filter(g => !gruposMap[g] && !audiencias.some(a => a.grupo === g))
      .map(g => { gruposMap[g] = []; return 'g:' + g; });
    ordenTop.unshift(...vacios);
    return { ordenTop, grupos: gruposMap };
  }

  const vistaAud = contenedoresAudDrag || buildContenedoresAud();

  function findContenedorAud(token, c) {
    if (token.startsWith('g:')) return '__top__';
    if (c.ordenTop.includes(token)) return '__top__';
    for (const nombre in c.grupos) if (c.grupos[nombre].includes(token)) return 'g:' + nombre;
    return null;
  }

  function handleDndAudStart({ active }) {
    const c = buildContenedoresAud();
    contenedoresAudRef.current = c;
    setContenedoresAudDrag(c);
    setActiveDragAudId(active.id);
  }

  function handleDndAudOver({ active, over }) {
    if (!over) return;
    const activeId = active.id; const overId = over.id;
    if (activeId.startsWith('g:')) return;
    const c = contenedoresAudRef.current;
    if (!c) return;
    const desde = findContenedorAud(activeId, c);
    let hacia = overId.startsWith('g:') ? overId : findContenedorAud(overId, c);
    if (!desde || !hacia || desde === hacia) return;
    const next = { ordenTop: [...c.ordenTop], grupos: {} };
    for (const k in c.grupos) next.grupos[k] = [...c.grupos[k]];
    if (desde === '__top__') { const i = next.ordenTop.indexOf(activeId); if (i >= 0) next.ordenTop.splice(i, 1); }
    else { const arr = next.grupos[desde.slice(2)]; const i = arr.indexOf(activeId); if (i >= 0) arr.splice(i, 1); }
    if (hacia === '__top__') { const i = next.ordenTop.indexOf(overId); next.ordenTop.splice(i >= 0 ? i : next.ordenTop.length, 0, activeId); }
    else { const arr = next.grupos[hacia.slice(2)]; const i = arr.indexOf(overId); arr.splice(i >= 0 ? i : arr.length, 0, activeId); }
    contenedoresAudRef.current = next;
    setContenedoresAudDrag(next);
  }

  function handleDndAudEnd({ active, over }) {
    const c = contenedoresAudRef.current;
    const activeId = active.id;
    if (c && over) {
      const overId = over.id;
      const next = { ordenTop: [...c.ordenTop], grupos: {} };
      for (const k in c.grupos) next.grupos[k] = [...c.grupos[k]];
      if (activeId.startsWith('g:')) {
        const from = next.ordenTop.indexOf(activeId);
        let destinoToken = overId;
        if (!next.ordenTop.includes(destinoToken)) destinoToken = findContenedorAud(overId, next) === '__top__' ? overId : null;
        const to = destinoToken ? next.ordenTop.indexOf(destinoToken) : -1;
        if (from >= 0 && to >= 0) next.ordenTop = arrayMove(next.ordenTop, from, to);
      } else {
        const cont = findContenedorAud(activeId, next);
        const arr = cont === '__top__' ? next.ordenTop : next.grupos[cont.slice(2)];
        const from = arr.indexOf(activeId);
        const to = arr.indexOf(overId);
        if (from >= 0 && to >= 0) { const moved = arrayMove(arr, from, to); if (cont === '__top__') next.ordenTop = moved; else next.grupos[cont.slice(2)] = moved; }
      }
      persistirOrdenAud(next);
    }
    contenedoresAudRef.current = null;
    setContenedoresAudDrag(null);
    setActiveDragAudId(null);
  }

  function persistirOrdenAud(c) {
    const flat = [];
    const ordenGrupos = [];
    for (const token of c.ordenTop) {
      if (token.startsWith('a:')) { flat.push({ id: token.slice(2), grupo: null }); }
      else { const nombre = token.slice(2); ordenGrupos.push(nombre); for (const mt of (c.grupos[nombre] || [])) flat.push({ id: mt.slice(2), grupo: nombre }); }
    }
    const updates = flat.map((f, i) => ({ id: f.id, grupo: f.grupo, orden: i }));
    const byId = Object.fromEntries(updates.map(u => [u.id, u]));
    setAudiencias(prev => prev.map(a => byId[a.id] ? { ...a, grupo: byId[a.id].grupo, orden: byId[a.id].orden } : a).sort((a, b) => (a.orden ?? 1e9) - (b.orden ?? 1e9)));
    setGruposAudienciasOrden(prev => { const resto = prev.filter(g => !ordenGrupos.includes(g)); return [...ordenGrupos, ...resto]; });
    Promise.all(updates.map(u => supabase.from('agenda').update({ grupo: u.grupo, orden: u.orden }).eq('id', u.id))).catch(() => fetchAudiencias());
  }

  // ── Render ──

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text4)' }}>Cargando...</div>;
  if (!tramite) return null;

  const estadoBadge = (e) => {
    const map = { pendiente: 'badge-amber', 'en espera': 'badge-blue', completado: 'badge-green' };
    return <span className={`badge ${map[e] || 'badge-gray'}`}>{e}</span>;
  };

  return (
    <>
      {/* HEADER */}
      <div className="drawer-header">
        {backLabel && (
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 12, cursor: 'pointer', padding: '0 0 10px 0', display: 'block' }}>
            {backLabel}
          </button>
        )}
        <div className="drawer-header-top">
          <div style={{ flex: 1 }}>
            <div className="drawer-num">Trámite extrajudicial</div>
            <div className="drawer-title">{tramite.nombre}</div>
            <div className="drawer-badges">
              {tramite.tramites_clientes?.map(tc => (
                <span
                  key={tc.cliente_id}
                  className="badge badge-gray"
                  onClick={() => tc.cliente_id && setVerCliente(tc.cliente_id)}
                  style={{ cursor: tc.cliente_id ? 'pointer' : 'default' }}
                >
                  {tc.clientes?.apellido} {tc.clientes?.nombre}
                </span>
              ))}
            </div>
          </div>
          <button className="drawer-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
          <button className="btn btn-primary" onClick={openEditarTramite} style={{ fontSize: 11, padding: '6px 14px', letterSpacing: 0.5 }}>
            EDITAR TRÁMITE
          </button>
        </div>
      </div>

      {/* TABS */}
      <div className="drawer-tabs">
        {[
          { id: 'tareas', label: `Tareas (${tareasPendientes.length})` },
          { id: 'audiencias', label: 'Audiencias', sub: `y Turnos (${audiencias.filter(a => (a.fecha || '') >= today).length})` },
          { id: 'ficha', label: 'Ficha y', sub: `notas (${notas.length})` },
        ].map(tab => (
          <div key={tab.id} className={`drawer-tab ${tabActiva === tab.id ? 'active' : ''}`} onClick={() => setTabActiva(tab.id)}>
            <div>{tab.label}</div>
            {tab.sub && <div style={{ paddingLeft: 6 }}>{tab.sub}</div>}
          </div>
        ))}
      </div>

      {/* BODY */}
      <div className="drawer-body">

        {/* TAB TAREAS */}
        {tabActiva === 'tareas' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12 }}>
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text4)', cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={mostrarCompletadas} onChange={e => setMostrarCompletadas(e.target.checked)} />
                  Ver completadas ({tareasCompletadas.length})
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text4)', cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={mostrarDescartadas} onChange={e => setMostrarDescartadas(e.target.checked)} />
                  Ver descartadas ({tareasDescartadas.length})
                </label>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {nuevoGrupoInlineOpen ? (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input className="form-input" style={{ height: 28, padding: '0 8px', fontSize: 12, width: 140 }} autoFocus
                      value={nuevoGrupoNombre} onChange={e => setNuevoGrupoNombre(e.target.value)} placeholder="Nombre del grupo"
                      onKeyDown={e => { if (e.key === 'Enter') crearGrupoInline(); if (e.key === 'Escape') { setNuevoGrupoInlineOpen(false); setNuevoGrupoNombre(''); } }}
                    />
                    <button className="btn btn-primary btn-sm" onClick={crearGrupoInline} disabled={!nuevoGrupoNombre.trim()}>Crear</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setNuevoGrupoInlineOpen(false); setNuevoGrupoNombre(''); }}>✕</button>
                  </div>
                ) : (
                  <button className="btn btn-ghost btn-sm" onClick={() => setNuevoGrupoInlineOpen(true)}>📁 Nuevo grupo</button>
                )}
                <button className="btn btn-primary btn-sm" onClick={() => openNuevaTarea()}>+ Nueva tarea</button>
              </div>
            </div>

            {tareasPendientes.length === 0 && !mostrarCompletadas && !mostrarDescartadas ? (
              <div className="empty-state" style={{ padding: 32 }}>
                <div className="empty-state-icon">📋</div>
                <div className="empty-state-title">Sin tareas pendientes</div>
              </div>
            ) : (
              <>
                <DndContext sensors={sensors} collisionDetection={colisionDnd} onDragStart={handleDndStart} onDragOver={handleDndOver} onDragEnd={handleDndEnd}
                  onDragCancel={() => { contenedoresRef.current = null; setContenedoresDrag(null); setActiveDragId(null); }}>
                  <SortableContext items={vista.ordenTop} strategy={verticalListSortingStrategy}>
                    {vista.ordenTop.map(token => {
                      if (token.startsWith('t:')) {
                        const t = tareas.find(x => x.id === token.slice(2));
                        if (!t) return null;
                        return (
                          <SortableTarea key={token} id={token} cardProps={{
                            tarea: t, onToggle: toggleTarea, onFijar: toggleFijada, onEdit: openEditTarea,
                            onDelete: esAdmin ? deleteTarea : null, onDescartar: descartarTarea, onVerDetalle: setTareaDetalleId,
                            onAsignar: () => openModalAsignar([t], t.titulo),
                            prioridadColor, formatDate, today,
                          }} />
                        );
                      }
                      const nombre = token.slice(2);
                      const memberTokens = vista.grupos[nombre] || [];
                      const tareasGrupo = memberTokens.map(mt => tareas.find(x => x.id === mt.slice(2))).filter(Boolean);
                      return (
                        <SortableGrupo
                          key={token} id={token} nombre={nombre}
                          memberTokens={memberTokens} tareasGrupo={tareasGrupo}
                          colapsado={gruposColapsados[nombre]}
                          onToggleColapso={() => setGruposColapsados(prev => ({ ...prev, [nombre]: !prev[nombre] }))}
                          renombrando={renombrandoGrupo === nombre}
                          renombreValor={renombreValor} setRenombreValor={setRenombreValor}
                          onRenombrarStart={() => { setRenombrandoGrupo(nombre); setRenombreValor(nombre); }}
                          onRenombrarConfirm={() => renombrarGrupo(nombre, renombreValor)}
                          onRenombrarCancel={() => { setRenombrandoGrupo(null); setRenombreValor(''); }}
                          onAsignarGrupo={() => openModalAsignar(tareasGrupo, nombre)}
                          onNuevaTarea={() => openNuevaTarea(nombre)}
                          onEliminar={() => eliminarGrupo(nombre)}
                          esAdmin={esAdmin}
                          cardPropsFor={(t) => ({
                            tarea: t, onToggle: toggleTarea, onFijar: toggleFijada, onEdit: openEditTarea,
                            onDelete: esAdmin ? deleteTarea : null, onDescartar: descartarTarea, onVerDetalle: setTareaDetalleId,
                            onAsignar: () => openModalAsignar([t], t.titulo),
                            prioridadColor, formatDate, today,
                          })}
                        />
                      );
                    })}
                  </SortableContext>
                  <DragOverlay>
                    {activeDragId && activeDragId.startsWith('t:') && (() => {
                      const t = tareas.find(x => x.id === activeDragId.slice(2));
                      return t ? <TareaCard tarea={t} prioridadColor={prioridadColor} formatDate={formatDate} today={today} overlay /> : null;
                    })()}
                    {activeDragId && activeDragId.startsWith('g:') && (
                      <div style={{ background: '#e2e6ea', border: '1px solid var(--blue)', borderRadius: 10, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}>
                        <span style={{ fontSize: 14, color: 'var(--text4)' }}>⠿</span>
                        <span style={{ fontSize: 13 }}>📁</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{activeDragId.slice(2)}</span>
                      </div>
                    )}
                  </DragOverlay>
                </DndContext>

                {mostrarCompletadas && tareasCompletadas.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '16px 0 8px', paddingTop: 12, borderTop: '0.5px solid var(--border)' }}>
                      Completadas ({tareasCompletadas.length})
                    </div>
                    {tareasCompletadas.map(t => (
                      <TareaCard key={t.id} tarea={t} onToggle={toggleTarea} onEdit={openEditTarea} onDelete={esAdmin ? deleteTarea : null} onVerDetalle={setTareaDetalleId} prioridadColor={prioridadColor} formatDate={formatDate} today={today} completada />
                    ))}
                  </>
                )}

                {mostrarDescartadas && tareasDescartadas.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '16px 0 8px', paddingTop: 12, borderTop: '0.5px solid var(--border)' }}>
                      Descartadas ({tareasDescartadas.length})
                    </div>
                    {tareasDescartadas.map(t => (
                      <TareaCard key={t.id} tarea={t} onReactivar={reactivarTarea} onDelete={esAdmin ? deleteTarea : null} onVerDetalle={setTareaDetalleId} prioridadColor={prioridadColor} formatDate={formatDate} today={today} descartada />
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* TAB AUDIENCIAS Y TURNOS */}
        {tabActiva === 'audiencias' && (() => {
          const todayStr = today;
          const proximaAudiencia = audiencias
            .filter(a => a.estado !== 'cancelado' && (a.fecha || '') >= todayStr)
            .sort((a, b) => (a.fecha || '') < (b.fecha || '') ? -1 : (a.fecha || '') > (b.fecha || '') ? 1 : (a.hora || '') < (b.hora || '') ? -1 : 1)[0] || null;
          return (
            <div>
              {/* Toolbar */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                  {proximaAudiencia ? (() => {
                    const dias = Math.round((new Date(proximaAudiencia.fecha + 'T12:00:00') - new Date(todayStr + 'T12:00:00')) / 86400000);
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text4)' }}>Próxima</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: dias <= 7 ? '#dc2626' : dias <= 14 ? '#ca8a04' : '#16a34a' }}>
                          {new Date(proximaAudiencia.fecha + 'T12:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    );
                  })() : (
                    <span style={{ fontSize: 12, color: 'var(--text4)' }}>Sin audiencias próximas</span>
                  )}
                  {audiencias.some(a => a.estado === 'realizado') && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text4)', cursor: 'pointer', userSelect: 'none' }}>
                      <input type="checkbox" checked={mostrarRealizadas} onChange={e => setMostrarRealizadas(e.target.checked)} />
                      Ver realizadas ({audiencias.filter(a => a.estado === 'realizado').length})
                    </label>
                  )}
                  {audiencias.some(a => a.estado === 'cancelado') && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text4)', cursor: 'pointer', userSelect: 'none' }}>
                      <input type="checkbox" checked={mostrarCanceladas} onChange={e => setMostrarCanceladas(e.target.checked)} />
                      Ver canceladas ({audiencias.filter(a => a.estado === 'cancelado').length})
                    </label>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
                  {nuevoGrupoAudienciaOpen ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input className="form-input" style={{ height: 28, padding: '0 8px', fontSize: 12, width: 130 }} autoFocus
                        value={nuevoGrupoAudienciaNombre} onChange={e => setNuevoGrupoAudienciaNombre(e.target.value)} placeholder="Nombre del grupo"
                        onKeyDown={e => { if (e.key === 'Enter') crearGrupoAudienciaInline(); if (e.key === 'Escape') { setNuevoGrupoAudienciaOpen(false); setNuevoGrupoAudienciaNombre(''); } }}
                      />
                      <button className="btn btn-primary btn-sm" disabled={!nuevoGrupoAudienciaNombre.trim()} onClick={crearGrupoAudienciaInline}>Crear</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setNuevoGrupoAudienciaOpen(false); setNuevoGrupoAudienciaNombre(''); }}>✕</button>
                    </div>
                  ) : (
                    <button className="btn btn-ghost btn-sm" onClick={() => setNuevoGrupoAudienciaOpen(true)}>📁 Nuevo grupo</button>
                  )}
                  <button className="btn btn-primary btn-sm" onClick={() => { setFormAudiencia({ titulo: '', tipo: 'Audiencia', fecha: '', hora: '00:00', notas: '', estado: 'pendiente', grupo: '' }); setEditingAudienciaId(null); setModalAudienciaOpen(true); }}>+ Nueva audiencia / turno</button>
                </div>
              </div>

              {audiencias.length === 0 ? (
                <div className="empty-state" style={{ padding: 32 }}>
                  <div className="empty-state-title">Sin audiencias ni turnos</div>
                  <div>Creá el primero con el botón de arriba</div>
                </div>
              ) : (
                <>
                  <DndContext sensors={sensors} collisionDetection={colisionDnd} onDragStart={handleDndAudStart} onDragOver={handleDndAudOver} onDragEnd={handleDndAudEnd}
                    onDragCancel={() => { contenedoresAudRef.current = null; setContenedoresAudDrag(null); setActiveDragAudId(null); }}>
                    <SortableContext items={vistaAud.ordenTop} strategy={verticalListSortingStrategy}>
                      {vistaAud.ordenTop.map(token => {
                        if (token.startsWith('a:')) {
                          const a = audiencias.find(x => x.id === token.slice(2));
                          if (!a) return null;
                          return (
                            <SortableAudiencia key={token} id={token} cardProps={{
                              a,
                              onClickCard: () => {},
                              onClickAsignar: () => openModalAsignar([{ id: a.id, titulo: a.titulo }], a.titulo, 'audiencia'),
                              onCambiarEstado: (estado) => setEstadoAudiencia(a.id, estado),
                              onDelete: () => deleteAudiencia(a.id),
                              esAdmin,
                            }} />
                          );
                        }
                        const nombre = token.slice(2);
                        const memberTokens = vistaAud.grupos[nombre] || [];
                        const audsGrupo = memberTokens.map(mt => audiencias.find(x => x.id === mt.slice(2))).filter(Boolean);
                        return (
                          <SortableGrupoAudiencia
                            key={token} id={token} nombre={nombre}
                            memberTokens={memberTokens} audsGrupo={audsGrupo}
                            colapsado={gruposAudienciasColapsados[nombre]}
                            onToggleColapso={() => setGruposAudienciasColapsados(prev => ({ ...prev, [nombre]: !prev[nombre] }))}
                            renombrando={renombrandoGrupoAudiencia === nombre}
                            renombreValor={renombreAudienciaValor} setRenombreValor={setRenombreAudienciaValor}
                            onRenombrarStart={() => { setRenombrandoGrupoAudiencia(nombre); setRenombreAudienciaValor(nombre); }}
                            onRenombrarConfirm={() => renombrarGrupoAudiencia(nombre, renombreAudienciaValor)}
                            onRenombrarCancel={() => { setRenombrandoGrupoAudiencia(null); setRenombreAudienciaValor(''); }}
                            onAsignarGrupo={() => openModalAsignar(audsGrupo.map(a => ({ id: a.id, titulo: a.titulo })), nombre, 'audiencia')}
                            onNuevaAudiencia={() => { setFormAudiencia({ titulo: '', tipo: 'Audiencia', fecha: '', hora: '', notas: '', estado: 'pendiente', grupo: nombre }); setEditingAudienciaId(null); setModalAudienciaOpen(true); }}
                            onEliminar={() => eliminarGrupoAudiencia(nombre)}
                            esAdmin={esAdmin}
                            cardPropsFor={(a) => ({
                              a,
                              onClickCard: () => {},
                              onClickAsignar: () => openModalAsignar([{ id: a.id, titulo: a.titulo }], a.titulo, 'audiencia'),
                              onCambiarEstado: (estado) => setEstadoAudiencia(a.id, estado),
                              onDelete: () => deleteAudiencia(a.id),
                              esAdmin,
                            })}
                          />
                        );
                      })}
                    </SortableContext>
                    <DragOverlay>
                      {activeDragAudId && activeDragAudId.startsWith('a:') && (() => {
                        const a = audiencias.find(x => x.id === activeDragAudId.slice(2));
                        return a ? <AudienciaCard a={a} overlay /> : null;
                      })()}
                      {activeDragAudId && activeDragAudId.startsWith('g:') && (
                        <div style={{ background: '#e2e6ea', border: '1px solid var(--blue)', borderRadius: 10, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}>
                          <span style={{ fontSize: 14, color: 'var(--text4)' }}>⠿</span>
                          <span style={{ fontSize: 13 }}>📁</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{activeDragAudId.slice(2)}</span>
                        </div>
                      )}
                    </DragOverlay>
                  </DndContext>

                  {mostrarRealizadas && audiencias.some(a => a.estado === 'realizado') && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '16px 0 8px', paddingTop: 12, borderTop: '0.5px solid var(--border)' }}>
                        Realizadas ({audiencias.filter(a => a.estado === 'realizado').length})
                      </div>
                      {audiencias.filter(a => a.estado === 'realizado').map(a => (
                        <AudienciaCard key={a.id} a={a}
                          onClickCard={() => {}}
                          onClickAsignar={() => openModalAsignar([{ id: a.id, titulo: a.titulo }], a.titulo, 'audiencia')}
                          onCambiarEstado={(estado) => setEstadoAudiencia(a.id, estado)}
                          onDelete={() => deleteAudiencia(a.id)}
                          esAdmin={esAdmin}
                        />
                      ))}
                    </>
                  )}

                  {mostrarCanceladas && audiencias.some(a => a.estado === 'cancelado') && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '16px 0 8px', paddingTop: 12, borderTop: '0.5px solid var(--border)' }}>
                        Canceladas ({audiencias.filter(a => a.estado === 'cancelado').length})
                      </div>
                      {audiencias.filter(a => a.estado === 'cancelado').map(a => (
                        <AudienciaCard key={a.id} a={a}
                          onClickCard={() => {}}
                          onClickAsignar={() => openModalAsignar([{ id: a.id, titulo: a.titulo }], a.titulo, 'audiencia')}
                          onCambiarEstado={(estado) => setEstadoAudiencia(a.id, estado)}
                          onDelete={() => deleteAudiencia(a.id)}
                          esAdmin={esAdmin}
                        />
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* TAB FICHA Y NOTAS */}
        {tabActiva === 'ficha' && (
          <div>
            {/* ── FICHA ── */}
            {(() => {
              const fichaItems = [
                { label: 'Tipo', value: tramite.tipo },
                { label: 'Estado', value: tramite.estado },
                { label: 'Fecha de inicio', value: formatDate(tramite.fecha_inicio) },
                { label: 'Fecha de vencimiento', value: formatDate(tramite.fecha_vencimiento) },
                { label: 'Parte contraria', value: tramite.parte_contraria },
                { label: 'Expediente vinculado', value: tramite.expedientes ? `${tramite.expedientes.numero ? tramite.expedientes.numero + ' — ' : ''}${tramite.expedientes.caratula || ''}`.trim() : null },
              ].filter(i => i.value);
              return (
                <>
                  {/* Trámite vinculado */}
                  <div style={{ marginBottom: 16, background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: (tramite.tramite_principal || subTramites.length > 0) ? 10 : 0 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Trámite vinculado
                      </span>
                    </div>
                    {!tramite.tramite_principal && subTramites.length === 0 && (
                      <div style={{ fontSize: 12, color: 'var(--text4)' }}>Este trámite no tiene vinculados.</div>
                    )}
                    {tramite.tramite_principal && (
                      <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 10, background: '#dbeafe', color: '#1d4ed8', borderRadius: 4, padding: '1px 7px', fontWeight: 600 }}>PRINCIPAL</span>
                        <span style={{ fontSize: 12, color: 'var(--blue)', cursor: 'default', fontWeight: 500 }}>
                          {tramite.tramite_principal.nombre}
                        </span>
                        {tramite.tramite_principal.tipo && (
                          <span style={{ fontSize: 11, color: 'var(--text4)' }}>— {tramite.tramite_principal.tipo}</span>
                        )}
                      </div>
                    )}
                    {subTramites.map(st => (
                      <div key={st.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 10, background: '#f3e8ff', color: '#7c3aed', borderRadius: 4, padding: '1px 7px', fontWeight: 600 }}>SUB-TRÁMITE</span>
                        <span style={{ fontSize: 12, color: 'var(--blue)', fontWeight: 500 }}>{st.nombre}</span>
                        {st.tipo && <span style={{ fontSize: 11, color: 'var(--text4)' }}>— {st.tipo}</span>}
                      </div>
                    ))}
                  </div>

                  {/* Info grid */}
                  {fichaItems.length > 0 && (
                    <div className="info-grid" style={{ marginBottom: 10 }}>
                      {fichaItems.map(item => (
                        <div className="info-item" key={item.label}><label>{item.label}</label><span>{item.value}</span></div>
                      ))}
                    </div>
                  )}
                  {/* Resultado */}
                  {tramite.resultado && (
                    <div style={{ marginBottom: 12, padding: '10px 14px', background: '#f0fdf4', border: '0.5px solid #bbf7d0', borderRadius: 8 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>Resultado</div>
                      <div style={{ fontSize: 13, color: '#166534', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{tramite.resultado}</div>
                    </div>
                  )}
                  {/* Botón modificar */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
                    <button className="btn btn-primary btn-sm" onClick={openEditarTramite}>
                      {fichaItems.length > 0 ? 'Modificar Ficha' : 'Agregar Ficha'}
                    </button>
                  </div>
                </>
              );
            })()}

            {/* ── NOTAS ── */}
            <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Notas</div>
              <div style={{ background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
                <textarea
                  className="form-textarea"
                  value={nuevaNota}
                  onChange={e => setNuevaNota(e.target.value)}
                  placeholder="Escribí una nueva nota..."
                  style={{ minHeight: 60, width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none', resize: 'vertical', outline: 'none', fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                  <button className="btn btn-primary btn-sm" onClick={addNota} disabled={!nuevaNota.trim()}>+ Agregar nota</button>
                </div>
              </div>
              {notas.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text4)', textAlign: 'center', padding: '14px 0' }}>Sin notas todavía</div>
              ) : (
                <div style={{ background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 8, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {notas.map(n => (
                    <div key={n.id} style={{ padding: '10px 12px', background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: 'var(--text4)', fontFamily: 'DM Mono' }}>
                          {new Date(n.created_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {esAdmin && <button className="btn btn-danger btn-xs" onClick={() => deleteNota(n.id)}>🗑</button>}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{n.contenido}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* MODAL TAREA */}
      {modalTareaOpen && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalTareaOpen(false)}>
          <div className="modal" style={{ width: 500 }}>
            <div className="modal-header">
              <div className="modal-title">{editingTareaId ? 'Editar tarea' : 'Nueva tarea'}</div>
              <button className="modal-close" onClick={() => setModalTareaOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group form-full">
                  <label className="form-label">Título *</label>
                  <input className="form-input" value={formTarea.titulo} onChange={e => setFormTarea(f => ({ ...f, titulo: e.target.value }))} placeholder="Descripción de la tarea" autoFocus />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Descripción</label>
                  <textarea className="form-textarea" value={formTarea.descripcion} onChange={e => setFormTarea(f => ({ ...f, descripcion: e.target.value }))} placeholder="Detalles adicionales..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha de Realización</label>
                  <input className="form-input" type="date" value={formTarea.fecha} onChange={e => setFormTarea(f => ({ ...f, fecha: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Vencimiento</label>
                  <input className="form-input" type="date" value={formTarea.fecha_vencimiento} onChange={e => setFormTarea(f => ({ ...f, fecha_vencimiento: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Prioridad</label>
                  <select className="form-select" value={formTarea.prioridad} onChange={e => setFormTarea(f => ({ ...f, prioridad: e.target.value }))}>
                    <option value="urgente">Urgente</option>
                    <option value="muy importante">Muy importante</option>
                    <option value="normal">Normal</option>
                    <option value="baja">Baja</option>
                  </select>
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 22 }}>
                  <input type="checkbox" id="fijada-tramite" checked={formTarea.fijada} onChange={e => setFormTarea(f => ({ ...f, fijada: e.target.checked }))} />
                  <label htmlFor="fijada-tramite" style={{ fontSize: 13, color: 'var(--text2)', cursor: 'pointer' }}>Fijar arriba</label>
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Grupo</label>
                  {!formTareaGrupoNuevo ? (
                    <select className="form-select" value={formTarea.grupo} onChange={e => { if (e.target.value === '__nuevo__') { setFormTareaGrupoNuevo(true); setFormTareaGrupoNuevoValor(''); } else { setFormTarea(f => ({ ...f, grupo: e.target.value })); } }}>
                      <option value="">Sin grupo</option>
                      {grupos.map(g => <option key={g} value={g}>{g}</option>)}
                      <option value="__nuevo__">Nuevo grupo...</option>
                    </select>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="form-input" autoFocus value={formTareaGrupoNuevoValor} onChange={e => setFormTareaGrupoNuevoValor(e.target.value)} placeholder="Nombre del nuevo grupo" />
                      <button className="btn btn-ghost btn-sm" onClick={() => { setFormTareaGrupoNuevo(false); setFormTareaGrupoNuevoValor(''); }}>✕</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {editingTareaId && (
              <div style={{ borderTop: '0.5px solid var(--border)', padding: '14px 20px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Notas</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <textarea className="form-textarea" value={nuevaNotaTarea} onChange={e => setNuevaNotaTarea(e.target.value)} placeholder="Agregar una nota..." style={{ minHeight: 60, flex: 1 }} />
                  <button className="btn btn-primary btn-sm" onClick={addNotaTarea} disabled={!nuevaNotaTarea.trim()} style={{ alignSelf: 'flex-end', flexShrink: 0 }}>Agregar</button>
                </div>
                {notasTarea.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text4)', textAlign: 'center', padding: '8px 0' }}>Sin notas</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                    {notasTarea.map(n => (
                      <div key={n.id} style={{ background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 7, padding: '8px 12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontSize: 10, color: 'var(--text4)', fontFamily: 'DM Mono' }}>
                            {new Date(n.created_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {esAdmin && <button className="btn btn-danger btn-xs" style={{ padding: '0px 5px' }} onClick={() => deleteNotaTarea(n.id)}>🗑</button>}
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{n.contenido}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModalTareaOpen(false)}>Cancelar</button>
              <button className="btn btn-primary" disabled={guardandoTarea} onClick={() => guardTarea(saveTarea)}>{guardandoTarea ? 'Guardando...' : 'Guardar tarea'}</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL AUDIENCIA */}
      {modalAudienciaOpen && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalAudienciaOpen(false)}>
          <div className="modal" style={{ width: 480 }}>
            <div className="modal-header">
              <div className="modal-title">{editingAudienciaId ? 'Editar audiencia / turno' : 'Nueva audiencia / turno'}</div>
              <button className="modal-close" onClick={() => setModalAudienciaOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              {audienciaError && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10, padding: '6px 10px', background: '#fee2e2', borderRadius: 6 }}>{audienciaError}</div>}
              <div className="form-grid">
                <div className="form-group form-full">
                  <label className="form-label">Título *</label>
                  <input className="form-input" value={formAudiencia.titulo} onChange={e => setFormAudiencia(f => ({ ...f, titulo: e.target.value }))} placeholder="Ej: Audiencia preliminar" autoFocus />
                </div>
                <div className="form-group">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={formAudiencia.tipo} onChange={e => setFormAudiencia(f => ({ ...f, tipo: e.target.value }))}>
                    <option>Audiencia</option>
                    <option>Consulta / Turno</option>
                    <option>Reunión</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Estado</label>
                  <select className="form-select" value={formAudiencia.estado} onChange={e => setFormAudiencia(f => ({ ...f, estado: e.target.value }))}>
                    <option value="pendiente">Pendiente</option>
                    <option value="realizado">Realizado</option>
                    <option value="cancelado">Cancelado</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha *</label>
                  <input className="form-input" type="date" value={formAudiencia.fecha} onChange={e => setFormAudiencia(f => ({ ...f, fecha: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Hora</label>
                  <input className="form-input" type="time" value={formAudiencia.hora} onChange={e => setFormAudiencia(f => ({ ...f, hora: e.target.value }))} />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Grupo</label>
                  {!formAudienciaGrupoNuevo ? (
                    <select className="form-select" value={formAudiencia.grupo} onChange={e => { if (e.target.value === '__nuevo__') { setFormAudienciaGrupoNuevo(true); setFormAudienciaGrupoNuevoValor(''); } else { setFormAudiencia(f => ({ ...f, grupo: e.target.value })); } }}>
                      <option value="">Sin grupo</option>
                      {gruposAudienciasOrden.map(g => <option key={g} value={g}>{g}</option>)}
                      <option value="__nuevo__">Nuevo grupo...</option>
                    </select>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="form-input" autoFocus value={formAudienciaGrupoNuevoValor} onChange={e => setFormAudienciaGrupoNuevoValor(e.target.value)} placeholder="Nombre del nuevo grupo" />
                      <button className="btn btn-ghost btn-sm" onClick={() => { setFormAudienciaGrupoNuevo(false); setFormAudienciaGrupoNuevoValor(''); }}>✕</button>
                    </div>
                  )}
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Notas</label>
                  <textarea className="form-textarea" value={formAudiencia.notas} onChange={e => setFormAudiencia(f => ({ ...f, notas: e.target.value }))} placeholder="Observaciones..." style={{ minHeight: 60 }} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModalAudienciaOpen(false)}>Cancelar</button>
              <button className="btn btn-primary" disabled={guardandoAudiencia} onClick={() => guardAudiencia(saveAudiencia)}>{guardandoAudiencia ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ASIGNAR */}
      {modalAsignarOpen && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalAsignarOpen(false)} style={{ zIndex: 1600 }}>
          <div className="modal" style={{ width: 460 }}>
            <div className="modal-header">
              <div className="modal-title">{asignarItems.length > 1 ? `Asignar grupo — ${asignarTitulo} (${asignarItems.length})` : 'Asignar'}</div>
              <button className="modal-close" onClick={() => setModalAsignarOpen(false)}>✕</button>
            </div>
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 20px' }}>
              {[['nueva', 'Nueva asignación'], ['existente', 'Agregar a existente']].map(([key, label]) => (
                <button key={key} onClick={() => setAsignarTab(key)} style={{ padding: '10px 16px', fontWeight: asignarTab === key ? 600 : 400, borderBottom: asignarTab === key ? '2px solid var(--blue)' : '2px solid transparent', background: 'none', border: 'none', cursor: 'pointer', color: asignarTab === key ? 'var(--blue)' : 'var(--text4)', fontSize: 13 }}>{label}</button>
              ))}
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: 14, padding: '8px 12px', background: 'var(--surface2)', borderRadius: 8, maxHeight: 100, overflowY: 'auto' }}>
                {asignarItems.map(t => <div key={t.id} style={{ fontSize: 12, color: 'var(--text2)', padding: '1px 0' }}>• {t.titulo}</div>)}
              </div>
              {asignarTab === 'nueva' ? (
                <>
                  <div className="form-group"><label className="form-label">Título</label><input className="form-input" value={asignarForm.titulo} onChange={e => setAsignarForm(f => ({ ...f, titulo: e.target.value }))} /></div>
                  <div className="form-group"><label className="form-label">Fecha de Realización</label><input type="date" className="form-input" value={asignarForm.fecha} onChange={e => setAsignarForm(f => ({ ...f, fecha: e.target.value }))} /></div>
                  <div className="form-group">
                    <label className="form-label">Asignar a</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {asignarUsuarios.map(u => (
                        <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                          <input type="checkbox" checked={asignarForm.asignados_a.includes(u.id)} onChange={e => setAsignarForm(f => ({ ...f, asignados_a: e.target.checked ? [...f.asignados_a, u.id] : f.asignados_a.filter(id => id !== u.id) })) } />
                          {u.nombre}
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="search-box" style={{ marginBottom: 10 }}><span style={{ color: 'var(--text4)' }}>⌕</span><input placeholder="Buscar asignación..." value={asignarBusqueda} onChange={e => setAsignarBusqueda(e.target.value)} autoComplete="off" /></div>
                  <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {asignarAsignaciones.filter(a => !asignarBusqueda || a.titulo.toLowerCase().includes(asignarBusqueda.toLowerCase())).map(a => (
                      <div key={a.id} onClick={() => setAsignarExistenteId(a.id)} style={{ padding: '8px 12px', borderRadius: 8, cursor: 'pointer', background: asignarExistenteId === a.id ? '#eff6ff' : 'var(--surface2)', border: `1px solid ${asignarExistenteId === a.id ? 'var(--blue)' : 'transparent'}` }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{a.titulo}</div>
                        <div style={{ fontSize: 11, color: 'var(--text4)' }}>{a.fecha}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModalAsignarOpen(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardarAsignar} disabled={asignarGuardando}>{asignarGuardando ? 'Guardando...' : asignarTab === 'nueva' ? 'Crear asignación' : 'Agregar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDITAR TRÁMITE */}
      {modalEditarTramite && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalEditarTramite(false)}>
          <div className="modal" style={{ width: 540, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <div className="modal-title">Editar trámite</div>
              <button className="modal-close" onClick={() => setModalEditarTramite(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto' }}>
              <div className="form-grid">
                <div className="form-group form-full">
                  <label className="form-label">Nombre *</label>
                  <input className="form-input" autoFocus value={formEditarTramite.nombre} onChange={e => setFormEditarTramite(f => ({ ...f, nombre: e.target.value }))} placeholder="Nombre del trámite" />
                </div>
                <div className="form-group">
                  <label className="form-label">Tipo</label>
                  <input className="form-input" value={formEditarTramite.tipo} onChange={e => setFormEditarTramite(f => ({ ...f, tipo: e.target.value }))} placeholder="Ej: Mediación, Conciliación..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Estado</label>
                  <select className="form-select" value={formEditarTramite.estado} onChange={e => setFormEditarTramite(f => ({ ...f, estado: e.target.value }))}>
                    <option value="pendiente">Pendiente</option>
                    <option value="en espera">En espera</option>
                    <option value="completado">Completado</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha de inicio</label>
                  <input className="form-input" type="date" value={formEditarTramite.fecha_inicio} onChange={e => setFormEditarTramite(f => ({ ...f, fecha_inicio: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha de vencimiento</label>
                  <input className="form-input" type="date" value={formEditarTramite.fecha_vencimiento} onChange={e => setFormEditarTramite(f => ({ ...f, fecha_vencimiento: e.target.value }))} />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Parte contraria</label>
                  <input className="form-input" value={formEditarTramite.parte_contraria} onChange={e => setFormEditarTramite(f => ({ ...f, parte_contraria: e.target.value }))} placeholder="Nombre de la parte contraria" />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Resultado</label>
                  <textarea className="form-textarea" value={formEditarTramite.resultado} onChange={e => setFormEditarTramite(f => ({ ...f, resultado: e.target.value }))} placeholder="Resultado del trámite (opcional)..." style={{ minHeight: 70 }} />
                </div>

                {/* Trámite vinculado */}
                <div className="form-group form-full" style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
                  <label className="form-label" style={{ color: 'var(--text3)', fontWeight: 700, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8, display: 'block' }}>
                    Vincular a trámite principal
                  </label>
                  {formEditarTramite.tramite_principal_id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, background: '#f3e8ff', border: '1px solid #c4b5fd' }}>
                      <span style={{ fontSize: 14 }}>🔗</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 500, marginBottom: 2 }}>Vinculado a:</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#5b21b6' }}>
                          {resultadosTramiteVincular.find(r => r.id === formEditarTramite.tramite_principal_id)?.nombre || tramite.tramite_principal?.nombre || '(cargando...)'}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ fontSize: 11, color: 'var(--red)', flexShrink: 0 }}
                        onClick={() => { setFormEditarTramite(f => ({ ...f, tramite_principal_id: null })); setBusquedaTramiteVincular(''); setResultadosTramiteVincular([]); }}
                      >
                        Desvincular
                      </button>
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <input
                        className="form-input"
                        placeholder="Buscar trámite para vincular..."
                        value={busquedaTramiteVincular}
                        onChange={e => { setBusquedaTramiteVincular(e.target.value); buscarTramitesVincular(e.target.value); }}
                        onBlur={() => setTimeout(() => setDropdownTramiteVincularOpen(false), 150)}
                        autoComplete="off"
                        style={{ fontSize: 13 }}
                      />
                      {dropdownTramiteVincularOpen && resultadosTramiteVincular.length > 0 && (
                        <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 300, maxHeight: 200, overflowY: 'auto', marginBottom: 4, boxShadow: '0 -4px 16px rgba(0,0,0,0.25)' }}>
                          {resultadosTramiteVincular.map(t => (
                            <div
                              key={t.id}
                              onMouseDown={() => {
                                setFormEditarTramite(f => ({ ...f, tramite_principal_id: t.id }));
                                setBusquedaTramiteVincular('');
                                setDropdownTramiteVincularOpen(false);
                              }}
                              style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)' }}
                            >
                              <div style={{ fontWeight: 500, color: 'var(--text)' }}>{t.nombre}</div>
                              {t.tipo && <div style={{ fontSize: 11, color: 'var(--text4)' }}>{t.tipo}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                      {dropdownTramiteVincularOpen && resultadosTramiteVincular.length === 0 && busquedaTramiteVincular.trim() && (
                        <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 300, padding: '10px 14px', marginBottom: 4, fontSize: 13, color: 'var(--text4)', boxShadow: '0 -4px 16px rgba(0,0,0,0.25)' }}>
                          Sin resultados
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 6 }}>
                    Al vincularlo, este trámite pasa a ser un sub-trámite del seleccionado.
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
              <div>
                <button className="btn btn-ghost" style={{ color: '#ef4444' }} onClick={() => {
                  setConfirm({
                    titulo: 'Eliminar trámite',
                    mensaje: `¿Eliminar "${tramite?.nombre}"? Esta acción no se puede deshacer.`,
                    onConfirmar: async () => {
                      setConfirm(null);
                      setModalEditarTramite(false);
                      await supabase.rpc('eliminar_tramite', { p_tramite_id: tramiteId });
                      onUpdate?.();
                      onClose?.();
                    }
                  });
                }}>
                  Eliminar trámite
                </button>
              </div>
              <div className="flex gap-2">
                <button className="btn btn-ghost" onClick={() => setModalEditarTramite(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={guardarEditarTramite} disabled={guardandoTramite}>
                  {guardandoTramite ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">✓ {toast}</div>}

      <ConfirmModal
        open={!!confirm}
        titulo={confirm?.titulo}
        mensaje={confirm?.mensaje}
        onConfirmar={confirm?.onConfirmar}
        onCancelar={() => setConfirm(null)}
      />

      {tareaDetalleId && (
        <TareaDetalle
          tareaId={tareaDetalleId}
          onClose={() => setTareaDetalleId(null)}
          onCompletar={() => { fetchTareas(); setTareaDetalleId(null); }}
          onEdit={(tarea) => { setTareaDetalleId(null); openEditTarea(tarea); }}
          grupos={gruposOrden}
          onCambiarGrupo={grupo => {
            const id = tareaDetalleId;
            const grupoNuevo = grupo || null;
            const grupoOriginal = tareas.find(t => t.id === id)?.grupo ?? null;
            setTareas(prev => prev.map(t => t.id === id ? { ...t, grupo: grupoNuevo } : t));
            supabase.from('tareas').update({ grupo: grupoNuevo }).eq('id', id)
              .then(({ error }) => { if (error) setTareas(prev => prev.map(t => t.id === id ? { ...t, grupo: grupoOriginal } : t)); });
          }}
        />
      )}

      {verCliente && (
        <>
          <div className="drawer-overlay" style={{ zIndex: 600 }} onMouseDown={() => setVerCliente(null)} />
          <div className="drawer" style={{ zIndex: 601 }}>
            <ClienteDetalle
              clienteId={verCliente}
              onClose={() => setVerCliente(null)}
              onEdit={(cliente) => { setClienteEdit(cliente); setVerCliente(null); }}
              backLabel="← Volver al trámite"
            />
          </div>
        </>
      )}

      <ClienteModal
        open={!!clienteEdit}
        editing={clienteEdit}
        onClose={() => setClienteEdit(null)}
        onSaved={() => { setClienteEdit(null); fetchTramite(); }}
      />
    </>
  );
}
